/**
 * FormattedClinicalText Component
 *
 * Renders clinical text with intelligent formatting for SOAP notes, imaging reports, etc.
 * Displays section headers, lists, vital signs badges, and proper line breaks.
 */

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { analyzeAndFormatClinicalText, FormattedElement } from '../utils/clinical-text-formatter';

interface FormattedClinicalTextProps {
  text: string;
  className?: string;
}

// Some notes (e.g. the synthetic red team charts) are authored in Markdown, which the
// SOAP-oriented heuristic formatter renders as literal `#`/`**` syntax. Detect ATX headers
// or bold spans and render those with react-markdown; plain-prose Epic notes (and the
// plain-text seed-copy notes) keep the existing clinical formatter.
const MARKDOWN_PATTERN = /^#{1,6}\s|\*\*[^*\n]+\*\*/m;

export const FormattedClinicalText: React.FC<FormattedClinicalTextProps> = ({
  text,
  className = '',
}) => {
  const isMarkdown = useMemo(() => MARKDOWN_PATTERN.test(text), [text]);

  const elements = useMemo(() => {
    if (isMarkdown) {
      return [];
    }
    try {
      return analyzeAndFormatClinicalText(text);
    } catch (error) {
      console.warn('Clinical text formatting failed, rendering plain text:', error);
      // Fallback to plain text with preserved whitespace
      return [
        {
          type: 'paragraph' as const,
          content: text,
          key: 'fallback',
        },
      ];
    }
  }, [text, isMarkdown]);

  if (isMarkdown) {
    return (
      <div
        className={`clinical-note-content prose prose-sm max-w-none text-gray-900 text-xs sm:text-sm ${className}`}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    );
  }

  if (elements.length === 0) {
    return null;
  }

  return (
    <div className={`clinical-note-content space-y-2 ${className}`}>
      {elements.map((element: FormattedElement) => {
        switch (element.type) {
          case 'header':
            return (
              <h4
                key={element.key}
                className="font-bold text-xs text-primary uppercase mt-3 first:mt-0"
              >
                {element.content}
              </h4>
            );

          case 'numbered-list':
            return (
              <ol key={element.key} className="list-decimal list-inside text-xs space-y-1 ml-2">
                {element.items?.map((item, idx) => (
                  <li key={`${element.key}-item-${idx}`}>{item}</li>
                ))}
              </ol>
            );

          case 'bullet-list':
            return (
              <ul key={element.key} className="list-disc list-inside text-xs space-y-1 ml-2">
                {element.items?.map((item, idx) => (
                  <li key={`${element.key}-item-${idx}`}>{item}</li>
                ))}
              </ul>
            );

          case 'vitals':
            return (
              <div key={element.key} className="flex flex-wrap gap-2 my-2">
                {element.signs?.map((sign, idx) => (
                  <div
                    key={`${element.key}-sign-${idx}`}
                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium"
                  >
                    <span className="font-semibold">{sign.type}:</span>
                    <span>
                      {sign.value} {sign.unit}
                    </span>
                  </div>
                ))}
              </div>
            );

          case 'line-break':
            return <div key={element.key} className="h-2" />;

          case 'paragraph':
            return (
              <p key={element.key} className="text-xs leading-relaxed">
                {element.content}
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};
